import React, { useState } from 'react';
import { clsx } from 'clsx';
import { CreateVendorDto, VendorCategoryMapping, VendorServiceArea } from '../../types/vendor';
import { vendorService } from '../../services/vendor/vendorService';
import { Button } from '../ui/Button';
import { X, Plus, Trash2 } from 'lucide-react';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const PRESET_CATEGORIES: VendorCategoryMapping[] = [
  { category_id: 'CAT-HVAC', category_name: 'HVAC & AC Systems', sub_categories: [] },
  { category_id: 'CAT-PLMB', category_name: 'Plumbing', sub_categories: [] },
  { category_id: 'CAT-ELEC', category_name: 'Electrical', sub_categories: [] },
  { category_id: 'CAT-CIVIL', category_name: 'Civil Works', sub_categories: [] },
  { category_id: 'CAT-MECH', category_name: 'Mechanical', sub_categories: [] },
  { category_id: 'CAT-PEST', category_name: 'Pest Control', sub_categories: [] },
  { category_id: 'CAT-CCTV', category_name: 'CCTV & Security', sub_categories: [] },
  { category_id: 'CAT-LIFT', category_name: 'Lifts & Elevators', sub_categories: [] },
];

const PRESET_AREAS: VendorServiceArea[] = [
  { location_id: 'LOC-001', location_name: 'Block A', zone: 'North' },
  { location_id: 'LOC-002', location_name: 'Block B', zone: 'North' },
  { location_id: 'LOC-003', location_name: 'Block C', zone: 'South' },
  { location_id: 'LOC-004', location_name: 'Block D', zone: 'South' },
  { location_id: 'LOC-005', location_name: 'Common Areas', zone: 'Central' },
];

export const VendorOnboardingModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState<CreateVendorDto>({
    name: '',
    trade_license_no: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    category_mappings: [],
    service_areas: [],
    notes: '',
  });

  const toggleCategory = (cat: VendorCategoryMapping) => {
    setForm(prev => {
      const exists = prev.category_mappings.some(c => c.category_id === cat.category_id);
      return {
        ...prev,
        category_mappings: exists
          ? prev.category_mappings.filter(c => c.category_id !== cat.category_id)
          : [...prev.category_mappings, cat],
      };
    });
  };

  const toggleArea = (area: VendorServiceArea) => {
    setForm(prev => {
      const exists = prev.service_areas.some(a => a.location_id === area.location_id);
      return {
        ...prev,
        service_areas: exists
          ? prev.service_areas.filter(a => a.location_id !== area.location_id)
          : [...prev.service_areas, area],
      };
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.createVendor(form);
      if (!res.success) throw new Error(res.message);
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to onboard vendor');
    } finally {
      setLoading(false);
    }
  };

  const isStep1Valid = form.name && form.trade_license_no && form.contact_person && form.contact_email && form.contact_phone;
  const isStep2Valid = form.category_mappings.length > 0;
  const isStep3Valid = form.service_areas.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900">
          <div>
            <h3 className="text-base font-semibold text-slate-100">Vendor Onboarding</h3>
            <p className="text-xs text-slate-400 mt-0.5">Step {step} of 3 — {step === 1 ? 'Company Details' : step === 2 ? 'Category Specialization' : 'Service Area Allocation'}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex border-b border-slate-800">
          {[1, 2, 3].map(s => (
            <div
              key={s}
              className={clsx(
                'flex-1 py-2 text-center text-xs font-semibold border-b-2 transition-colors',
                step === s
                  ? 'text-sky-400 border-sky-500'
                  : step > s
                  ? 'text-emerald-400 border-emerald-500'
                  : 'text-slate-500 border-transparent'
              )}
            >
              {s === 1 ? 'Details' : s === 2 ? 'Categories' : 'Areas'}
            </div>
          ))}
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-3">
              {[
                { label: 'Company Name *', key: 'name', placeholder: 'Alpha Technical Services LLC' },
                { label: 'Trade License No. *', key: 'trade_license_no', placeholder: 'TL-2024-XXXX' },
                { label: 'Contact Person *', key: 'contact_person', placeholder: 'Mohammed Al-Rashid' },
                { label: 'Contact Email *', key: 'contact_email', placeholder: 'info@company.ae', type: 'email' },
                { label: 'Contact Phone *', key: 'contact_phone', placeholder: '+971-50-XXXXXXX' },
                { label: 'Business Address', key: 'address', placeholder: 'Industrial Area 4, Sharjah' },
              ].map(({ label, key, placeholder, type }) => (
                <div key={key}>
                  <label className="block text-xs text-slate-400 mb-1">{label}</label>
                  <input
                    type={type || 'text'}
                    value={(form as any)[key]}
                    onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500 transition-colors"
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Notes / Remarks</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  placeholder="Additional notes..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500 transition-colors resize-none"
                />
              </div>
            </div>
          )}

          {/* Step 2 – Category selection */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-xs text-slate-400">Select all categories this vendor specializes in:</p>
              <div className="grid grid-cols-2 gap-2">
                {PRESET_CATEGORIES.map(cat => {
                  const selected = form.category_mappings.some(c => c.category_id === cat.category_id);
                  return (
                    <button
                      key={cat.category_id}
                      onClick={() => toggleCategory(cat)}
                      className={clsx(
                        'flex items-center gap-2 p-3 rounded-xl border text-left text-sm font-medium transition-colors',
                        selected
                          ? 'bg-sky-500/10 border-sky-500/20 text-sky-400'
                          : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-600'
                      )}
                    >
                      <span className={clsx(
                        'w-4 h-4 rounded border flex items-center justify-center text-[10px] flex-shrink-0',
                        selected ? 'border-sky-500 bg-sky-500 text-white' : 'border-slate-600 bg-transparent text-transparent'
                      )}>
                        ✓
                      </span>
                      {cat.category_name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3 – Service areas */}
          {step === 3 && (
            <div className="space-y-3">
              <p className="text-xs text-slate-400">Select service areas this vendor will cover:</p>
              <div className="space-y-2">
                {PRESET_AREAS.map(area => {
                  const selected = form.service_areas.some(a => a.location_id === area.location_id);
                  return (
                    <button
                      key={area.location_id}
                      onClick={() => toggleArea(area)}
                      className={clsx(
                        'w-full flex items-center gap-3 p-3 rounded-xl border text-left text-sm transition-colors',
                        selected
                          ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                          : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-600'
                      )}
                    >
                      <span className={clsx(
                        'w-4 h-4 rounded border flex items-center justify-center text-[10px] flex-shrink-0',
                        selected ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-600 bg-transparent text-transparent'
                      )}>
                        ✓
                      </span>
                      <span className="font-medium">{area.location_name}</span>
                      <span className="ml-auto text-xs opacity-60">{area.zone}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {error && <p className="text-xs text-rose-400 pt-1">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-slate-800 bg-slate-950/60">
          <Button variant="outline" onClick={step === 1 ? onClose : () => setStep(prev => (prev - 1) as 1 | 2 | 3)}>
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          {step < 3 ? (
            <Button
              variant="primary"
              onClick={() => setStep(prev => (prev + 1) as 2 | 3)}
              disabled={step === 1 ? !isStep1Valid : !isStep2Valid}
            >
              Next →
            </Button>
          ) : (
            <Button variant="primary" onClick={handleSubmit} disabled={loading || !isStep3Valid}>
              {loading ? 'Submitting…' : 'Submit Vendor'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
